import type { ApiKeyInfo, CreateApiKeyRequest, CreatedApiKey } from '@mcgames/contracts';
import { Badge, Button, Dialog, Spinner, TextInput } from '@mcgames/ui';
import { useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApiMutation, useApiQuery } from '../api/hooks.ts';
import { formatDateTime } from '../lib/format-date.ts';
import styles from './Admin.module.css';
import { ConfirmDialog } from './ConfirmDialog.tsx';
import { ErrorText } from './games/ErrorText.tsx';

const KEYS_PATH = '/api/admin/api-keys';
const NAME_MAX_LENGTH = 100;

export function ApiKeysPage() {
  const { t } = useTranslation('admin');
  const keys = useApiQuery<ApiKeyInfo[]>(KEYS_PATH);

  return (
    <div className={styles['page']}>
      <h1 className={styles['heading']}>{t('apiKeys.title')}</h1>
      <p className={styles['muted']}>{t('apiKeys.intro')}</p>
      <CreateKey />
      {keys.isPending && <Spinner />}
      {keys.isError && (
        <p role="alert" className={styles['error']}>
          {t('apiKeys.loadFailed')}
        </p>
      )}
      {keys.data?.length === 0 && <p className={styles['muted']}>{t('apiKeys.empty')}</p>}
      {keys.data !== undefined && keys.data.length > 0 && (
        <div className={styles['tableWrap']}>
          <table className={styles['table']} aria-label={t('apiKeys.label')}>
            <thead>
              <tr>
                <th scope="col">{t('apiKeys.cols.name')}</th>
                <th scope="col">{t('apiKeys.cols.key')}</th>
                <th scope="col">{t('apiKeys.cols.createdBy')}</th>
                <th scope="col">{t('apiKeys.cols.created')}</th>
                <th scope="col">{t('apiKeys.cols.lastUsed')}</th>
                <th scope="col">{t('apiKeys.cols.status')}</th>
                <th scope="col">{t('apiKeys.cols.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {keys.data.map((key) => (
                <KeyRow key={key.id} apiKey={key} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function CreateKey() {
  const { t } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  // The only copy of the key in the UI; dropped when the dialog closes.
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const create = useApiMutation<CreateApiKeyRequest, CreatedApiKey>('POST', KEYS_PATH, {
    onSuccess: (key) => {
      setCreated(key);
      setName('');
      void queryClient.invalidateQueries({ queryKey: [KEYS_PATH] });
    },
  });
  const trimmed = name.trim();

  return (
    <>
      <form
        className={styles['card']}
        onSubmit={(event) => {
          event.preventDefault();
          if (trimmed !== '' && !create.isPending) create.mutate({ name: trimmed });
        }}
      >
        <div className={styles['row']}>
          <div className={styles['grow']}>
            <TextInput
              label={t('apiKeys.name')}
              value={name}
              maxLength={NAME_MAX_LENGTH}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
          <Button type="submit" disabled={trimmed === '' || create.isPending}>
            {t('apiKeys.create')}
          </Button>
        </div>
        <ErrorText error={create.error} />
      </form>
      {created !== null && (
        <NewKeyDialog key={created.id} apiKey={created} onClose={() => setCreated(null)} />
      )}
    </>
  );
}

type CopyState = 'idle' | 'copied' | 'failed';

/** Shows a newly created key once, with a copy button; a refused clipboard selects the key instead. */
function NewKeyDialog({ apiKey, onClose }: { apiKey: CreatedApiKey; onClose: () => void }) {
  const { t } = useTranslation('admin');
  const field = useRef<HTMLDivElement>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');

  async function copy() {
    try {
      await navigator.clipboard.writeText(apiKey.key);
      setCopyState('copied');
    } catch {
      const input = field.current?.querySelector('input');
      input?.focus();
      input?.select();
      setCopyState('failed');
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('apiKeys.created.title')}
      footer={
        <Button type="button" onClick={onClose}>
          {t('apiKeys.created.done')}
        </Button>
      }
    >
      <p>
        <strong>{t('apiKeys.created.warning')}</strong>
      </p>
      <div className={styles['row']}>
        <div className={styles['grow']} ref={field}>
          <TextInput
            label={t('apiKeys.created.keyLabel')}
            value={apiKey.key}
            readOnly
            spellCheck={false}
            autoComplete="off"
            onFocus={(event) => event.target.select()}
          />
        </div>
        <Button type="button" variant="secondary" onClick={() => void copy()}>
          {t('apiKeys.created.copy')}
        </Button>
      </div>
      <p role="status" className={copyState === 'failed' ? styles['error'] : styles['status']}>
        {copyState === 'copied' && t('apiKeys.created.copied')}
        {copyState === 'failed' && t('apiKeys.created.copyFailed')}
      </p>
      <p className={styles['muted']}>{t('apiKeys.created.usage')}</p>
    </Dialog>
  );
}

function KeyRow({ apiKey }: { apiKey: ApiKeyInfo }) {
  const { t, i18n } = useTranslation('admin');
  const queryClient = useQueryClient();
  const [asking, setAsking] = useState(false);
  const revoke = useApiMutation<void>('DELETE', `${KEYS_PATH}/${encodeURIComponent(apiKey.id)}`, {
    onSuccess: () => {
      setAsking(false);
      void queryClient.invalidateQueries({ queryKey: [KEYS_PATH] });
    },
  });
  const revoked = apiKey.revokedAt !== null;

  return (
    <tr>
      <td className={styles['wrap']}>{apiKey.name}</td>
      <td className={styles['nowrap']}>
        <code>{`${apiKey.prefix}…`}</code>
      </td>
      <td className={styles['wrap']}>{apiKey.createdBy}</td>
      <td className={styles['nowrap']}>{formatDateTime(apiKey.createdAt, i18n.language)}</td>
      <td className={styles['nowrap']}>
        {apiKey.lastUsedAt === null
          ? t('apiKeys.never')
          : formatDateTime(apiKey.lastUsedAt, i18n.language)}
      </td>
      <td>
        <Badge tone={revoked ? 'danger' : 'accent'}>
          {revoked ? t('apiKeys.revoked') : t('apiKeys.active')}
        </Badge>
      </td>
      <td>
        {!revoked && (
          <Button
            size="sm"
            variant="secondary"
            aria-label={t('apiKeys.revoke', { name: apiKey.name })}
            onClick={() => {
              revoke.reset();
              setAsking(true);
            }}
          >
            {t('apiKeys.revokeConfirm')}
          </Button>
        )}
        {asking && (
          <ConfirmDialog
            title={t('apiKeys.revokeTitle')}
            message={t('apiKeys.revokeMessage', { name: apiKey.name })}
            confirmLabel={t('apiKeys.revokeConfirm')}
            pending={revoke.isPending}
            error={revoke.error}
            onConfirm={() => revoke.mutate()}
            onCancel={() => setAsking(false)}
          />
        )}
      </td>
    </tr>
  );
}
